import { supabase } from "@/lib/supabaseClient";

export type OutreachNetwork = "instagram" | "facebook" | "tiktok";
export const OUTREACH_NETWORKS: { value: OutreachNetwork; label: string; icon: string }[] = [
  { value: "instagram", label: "Instagram", icon: "ri-instagram-line" },
  { value: "facebook", label: "Facebook", icon: "ri-facebook-circle-line" },
  { value: "tiktok", label: "TikTok", icon: "ri-tiktok-line" },
];

export type OutreachSettings = { caps: Record<OutreachNetwork, number>; pauseSeconds: number };

export const DEFAULT_SETTINGS: OutreachSettings = { caps: { instagram: 100, facebook: 100, tiktok: 100 }, pauseSeconds: 90 };

export function networkOf(platform: string): OutreachNetwork | null {
  const p = platform.toLowerCase();
  return p === "instagram" || p === "facebook" || p === "tiktok" ? p : null;
}

async function me(): Promise<{ id: string; orgId: string | null }> {
  const { data } = await supabase.auth.getUser();
  const id = data.user?.id;
  if (!id) throw new Error("Tu sesión expiró. Vuelve a iniciar sesión.");
  const { data: prof } = await supabase.from("profiles").select("organization_id").eq("id", id).maybeSingle();
  return { id, orgId: (prof?.organization_id as string | null) ?? null };
}

export async function fetchSettings(): Promise<OutreachSettings> {
  const { data, error } = await supabase.from("outreach_settings").select("daily_cap, cap_facebook, cap_tiktok, pause_seconds").maybeSingle();
  if (error) throw error;
  if (!data) return DEFAULT_SETTINGS;
  return {
    caps: { instagram: data.daily_cap as number, facebook: data.cap_facebook as number, tiktok: data.cap_tiktok as number },
    pauseSeconds: data.pause_seconds as number,
  };
}

const clampCap = (n: number) => Math.min(200, Math.max(1, Math.round(Number.isFinite(n) ? n : 100)));

export async function saveSettings(s: OutreachSettings): Promise<OutreachSettings> {
  const user = await me();
  if (!user.orgId) throw new Error("No se encontró tu organización.");
  const caps = { instagram: clampCap(s.caps.instagram), facebook: clampCap(s.caps.facebook), tiktok: clampCap(s.caps.tiktok) };
  const pauseSeconds = Math.min(900, Math.max(30, Math.round(s.pauseSeconds)));
  const { error } = await supabase.from("outreach_settings").upsert(
    {
      user_id: user.id,
      organization_id: user.orgId,
      daily_cap: caps.instagram,
      cap_facebook: caps.facebook,
      cap_tiktok: caps.tiktok,
      pause_seconds: pauseSeconds,
      updated_at: new Date().toISOString(),
    },
    { onConflict: "user_id" },
  );
  if (error) throw error;
  return { caps, pauseSeconds };
}
/** Ids de prospectos en cola (en el orden en que se agregaron). */
export async function fetchQueueIds(): Promise<string[]> {
  const user = await me();
  const { data, error } = await supabase
    .from("send_queue")
    .select("prospect_id")
    .eq("owner_id", user.id)
    .eq("status", "queued")
    .order("position", { ascending: true });
  if (error) throw error;
  return (data ?? []).map((r) => r.prospect_id as string);
}

export async function addToQueue(prospectIds: string[]): Promise<void> {
  if (prospectIds.length === 0) return;
  const user = await me();
  if (!user.orgId) throw new Error("No se encontró tu organización.");
  const rows = prospectIds.map((id) => ({
    organization_id: user.orgId,
    owner_id: user.id,
    prospect_id: id,
    status: "queued",
    done_at: null,
  }));
  const { error } = await supabase.from("send_queue").upsert(rows, { onConflict: "owner_id,prospect_id" });
  if (error) throw error;
}

export async function setQueueStatus(prospectId: string, status: "done" | "skipped" | "queued"): Promise<void> {
  const user = await me();
  const { error } = await supabase
    .from("send_queue")
    .update({ status, done_at: status === "queued" ? null : new Date().toISOString() })
    .eq("owner_id", user.id)
    .eq("prospect_id", prospectId);
  if (error) throw error;
}

export async function removeFromQueue(prospectId: string): Promise<void> {
  const user = await me();
  const { error } = await supabase.from("send_queue").delete().eq("owner_id", user.id).eq("prospect_id", prospectId);
  if (error) throw error;
}

export type NetworkStats = { sentToday: number; lastSentAt: string | null };
export type TodayStats = Record<OutreachNetwork, NetworkStats>;

export const EMPTY_STATS: TodayStats = {
  instagram: { sentToday: 0, lastSentAt: null },
  facebook: { sentToday: 0, lastSentAt: null },
  tiktok: { sentToday: 0, lastSentAt: null },
};

/** Primeros mensajes marcados como enviados hoy (hora local) por esta persona, por red y desde cualquier pantalla. */
export async function fetchTodayStats(): Promise<TodayStats> {
  const user = await me();
  const start = new Date();
  start.setHours(0, 0, 0, 0);
  const { data, error } = await supabase
    .from("prospects")
    .select("opener_sent_at, network")
    .eq("owner_id", user.id)
    .gte("opener_sent_at", start.toISOString())
    .order("opener_sent_at", { ascending: false });
  if (error) throw error;
  const out: TodayStats = {
    instagram: { sentToday: 0, lastSentAt: null },
    facebook: { sentToday: 0, lastSentAt: null },
    tiktok: { sentToday: 0, lastSentAt: null },
  };
  for (const r of data ?? []) {
    const n = networkOf(String(r.network ?? ""));
    if (!n) continue;
    out[n].sentToday += 1;
    if (!out[n].lastSentAt) out[n].lastSentAt = r.opener_sent_at as string;
  }
  return out;
}
/** Segundos que faltan para poder enviar el siguiente. La pausa varía ±25 % de forma estable (no se puede "refrescar" para saltarla). */
export function cooldownRemaining(lastSentAt: string | null, pauseSeconds: number, now = Date.now()): number {
  if (!lastSentAt) return 0;
  const last = Date.parse(lastSentAt);
  if (!Number.isFinite(last)) return 0;
  const factor = 0.75 + (last % 51) / 100; // 0.75 – 1.25
  const total = Math.round(pauseSeconds * factor);
  return Math.max(0, Math.ceil((last + total * 1000 - now) / 1000));
}